import { find, forEach, get, map } from 'lodash';
import {
  Field,
  FieldTable,
  FieldTableColumn,
  FieldTableHeading,
  FieldTableRow,
  FieldValidation,
} from '@/types/Field';
import {
  OpportunityFieldResponse,
  OpportunityResponse,
} from '@/store/modules/opportunities/types';
import { formatDataValue } from './format';
import { FormatOptions } from '@/types/Global';
import { FieldType } from '@/enums/Global';

export const fieldTableGeneratorV2 = (opportunity: OpportunityResponse) => {
  const rowNumbers = Array.from(
    new Set(opportunity.fields.map(field => field.section.dataValue as number))
  );

  return rowNumbers.map(row => {
    const columns = opportunity.fields.filter(
      field => field.section.dataValue === row
    );

    return columns.map(field => {
      return {
        isEditable: formatDataValue(field.isEditable),
        isOptional: formatDataValue(field.isOptional),
        isHidden: formatDataValue(field.isHidden),
        fieldType: formatDataValue(field.fieldType),
        priority: formatDataValue(field.priority, {
          keepZeroAsZero: true,
        } as FormatOptions),
        name: formatDataValue(field.name),
        value: formatDataValue(field.value, {
          bypassFormatting: true,
        } as FormatOptions),
        valueType: get(field, 'value.dataType', 'string'),
        validation: get(field, 'validation', {} as FieldValidation),
        warning: formatDataValue(field.warning),
      } as Field;
    });
  });
};

export const fieldTableGenerator = (
  columnIdentifiers: Array<string>,
  opportunity: OpportunityResponse
) => {
  const fieldHeadings = map(columnIdentifiers, headingKey => {
    return {
      key: headingKey,
    } as FieldTableHeading;
  });

  const fieldRows = map(opportunity[columnIdentifiers[0]], field => {
    const rowKey = field.name.dataValue;
    return {
      key: rowKey,
      columns: map(columnIdentifiers, columnKey => {
        const columnData = find(
          opportunity[columnKey],
          e => e.name.dataValue === field.name.dataValue
        ) as OpportunityFieldResponse;

        return {
          key: `${columnKey}.${rowKey}`,
          field: {
            isEditable: formatDataValue(columnData.isEditable),
            fieldType: formatDataValue(columnData.fieldType),
            priority: formatDataValue(columnData.priority, {
              keepZeroAsZero: true,
            } as FormatOptions),
            name: formatDataValue(columnData.name),
            value: formatDataValue(columnData.value, {
              bypassFormatting: true,
            } as FormatOptions),
            valueType: get(columnData, 'value.dataType', 'string'),
            validation: get(columnData, 'validation', {} as FieldValidation),
            warning: formatDataValue(columnData.warning),
          } as Field,
        } as FieldTableColumn;
      }),
    } as FieldTableRow;
  });

  return {
    headings: fieldHeadings,
    rows: fieldRows.filter(row => {
      const fieldTypeMap = row.columns.map(column => column.field.fieldType);
      return !fieldTypeMap.every(fieldType => fieldType === FieldType.HIDDEN);
    }),
  } as FieldTable;
};

export const arrayToKeyValue = (fields: Array<OpportunityFieldResponse>) => {
  const returnObject = {};
  forEach(fields, field => {
    const fieldName = get(field, 'name.dataValue', null);
    if (fieldName) {
      const fieldValue = get(field, 'value.dataValue', null);
      returnObject[fieldName] = fieldValue;
    }
  });
  return returnObject;
};
